import {
    Box,
    FormControl,
    FormHelperText,
    OutlinedInput,
    Typography,
} from '@mui/material'
import TruncatedText from './TruncatedText'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../redux/hook'
import { AttributeType } from '../../constants/constants'
import currencyCodes from 'currency-codes'
import SearchAndSelectInput from './SearchAndSelectInput'
import { useTranslation } from 'react-i18next'

interface CurrencyFieldProps {
    id: string
    label: string
    value: string
    onChange: any
    type: string
    defaultUnits: any
    required?: boolean
    placeholder?: string
    disabled?: boolean
    errorMessage?: string
    range?: string
}

const CurrencyField = (props: CurrencyFieldProps): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        type = 'decimal',
        defaultUnits,
        required,
        placeholder,
        disabled,
        errorMessage = '',
        range = '',
    } = props
    const { t } = useTranslation()

    const user = useAppSelector((state) => state.user.user)

    const [val, setVal] = useState<string>('')
    const [unit, setUnit] = useState<string>('')

    const initialCurrencyList = useMemo(() => {
        const currencyCodesList = currencyCodes.data.map((currency: any) => {
            return { id: currency.code, name: currency.code }
        })
        return currencyCodesList
    }, [])

    const [currencyList, setCurrencyList] = useState<any[]>([])

    useEffect(() => {
        setCurrencyList(initialCurrencyList)
    }, [initialCurrencyList])

    useEffect(() => {
        if (value !== null && value !== undefined) {
            setVal(value)
        } else {
            setVal('')
        }
    }, [value])

    useEffect(() => {
        if (defaultUnits) {
            setUnit(defaultUnits)
        } else {
            const userUnits = user?.preferences?.units
            setUnit(userUnits ? userUnits[AttributeType.CURRENCY] || '' : '')
        }
    }, [defaultUnits])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedVal = e.target.value
        onChange(id, updatedVal)
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key, currentTarget } = e

        // Allow control keys like Backspace, Tab, and Arrow keys
        if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(key)) {
            return
        }
        if (type === 'number' && (key === '.' || key === '-')) {
            e.preventDefault()
        }

        const inputValue = currentTarget.value

        // Allow digits and a single dot for decimals
        if (/[\d.]/.test(key)) {
            // Prevent multiple dots
            if (key === '.' && inputValue.includes('.')) {
                e.preventDefault()
            }
        }
        // Allow a single '-' at the start for negative numbers
        else if (key === '-' && inputValue === '') {
            return
        }
        // Block all other inputs
        else {
            e.preventDefault()
        }
    }

    const onSelectUnits = (id: string, value: string) => {
        onChange(id, value, 'unit')
    }
    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
                <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                    {label}
                    {required ? '*' : ''}
                </Typography>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: '100%' }}>
                        <OutlinedInput
                            size="small"
                            id={id}
                            label=""
                            type="text"
                            value={val}
                            onKeyDown={handleKeyPress}
                            onChange={handleChange}
                            sx={{
                                width: '100%',
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                    {
                                        display: 'none',
                                    },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& ::placeholder': {
                                    // opacity: 0.14,
                                },
                            }}
                            placeholder={
                                placeholder
                                    ? placeholder
                                    : t('enterLabel', { label })
                            }
                            inputProps={{
                                style: {
                                    fontSize: 14,
                                    opacity:
                                        val !== '' && val !== null
                                            ? '1'
                                            : '0.4',
                                },
                                type: `number`,
                                onKeyPress: (e) => {
                                    if (e.key === 'Enter') {
                                        // onEnter(
                                        //     id,
                                        //     (e.target as HTMLInputElement).value,
                                        // )

                                        // write your functionality here
                                        e.preventDefault()
                                    }
                                },
                            }}
                            disabled={!!disabled}
                            error={errorMessage !== ''}
                        />
                        {range?.trim() && (
                            <FormHelperText>
                                <TruncatedText
                                    sx={{ fontSize: '12px' }}
                                    text={t('acceptedValues', { range })}
                                />
                            </FormHelperText>
                        )}
                        {errorMessage !== '' && (
                            <FormHelperText sx={{ color: 'red' }}>
                                {errorMessage}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box sx={{ width: '130px' }}>
                        <SearchAndSelectInput
                            label=""
                            id={id}
                            value={unit}
                            list={currencyList}
                            list_Height="200px"
                            disabled={disabled}
                            onSelect={onSelectUnits}
                            errorMessage={
                                !unit.length && errorMessage ? errorMessage : ''
                            }
                        />
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}

export default CurrencyField
